/* eslint-disable indent */
import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
} from '@material-ui/core';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { STRING_TRUE } from '@mummssoftware/utils/constants';
import { AppContext } from '../../App/AppContext';

const useStyles = makeStyles((theme) => ({
  stickyHeader: {
    backgroundColor: theme.palette.background.header,
    color: theme.palette.grid.headerFontColor,
    height: '2rem',
    fontWeight: 600,
  },
  history: {
    backgroundColor: theme.palette.background.header,
    height: '2rem',
  },
}));

const StyledTableHead = styled(TableHead)`
  && {
    font-size: 16px;
    padding: none;
  }
  ${(props) =>
    /**
     * This css hides the header row for the historical tables while allowing it to stay
     * inline with the parent table columns.
     * visibility hidden hides the content of the content of the header.
     * display:table-footer-group moves the header row to the footer.  This allows for a
     * flush display at the top of the historical table with the parent and adds whitespace
     * to the bottom to help differenticate from the next med group when open.
     */
    props['data-hide']
      ? `&& {
        visibility: hidden;
        display: table-footer-group;
      }`
      : `;`}
`;

const StyledTableCell = styled(TableCell)`
  ${(props) =>
    props.smallwidget // react wants custom props to dom in lowercase
      ? `&& {
        padding: 0px 4px 0px 4px;
      }`
      : `&& {
    font-size: 16px;
    padding: 6px;
  }`}
`;

export const Headers = ({
  messages,
  order,
  orderBy,
  onRequestSort,
  history,
  showStartReasonColumn = true,
}) => {
  // eslint-disable-next-line consistent-return
  const createSortHandler = (property) => (event) => {
    if (onRequestSort) {
      onRequestSort(event, property);
    } else return null;
  };
  const { smallWidget, rcopiaConfig } = useContext(AppContext);
  const { hidecovered } = rcopiaConfig;
  const classes = useStyles();
  const coveredSymbol = <MonetizationOnIcon fontSize="small" />;
  // const minStandardsMetSymbol = <StarsIcon fontSize="small" />;

  const headCells = smallWidget
    ? [
        {
          id: 'noteCounts',
          numeric: false,
          disablePadding: false,
          label: 'note counts',
          width: '3%',
          align: 'left',
          sort: false,
          show: false,
        },
        {
          id: 'approval',
          numeric: false,
          disablePadding: false,
          label: 'approval',
          width: '3%',
          align: 'left',
          sort: false,
          show: false,
        },
        {
          id: 'name',
          numeric: false,
          disablePadding: false,
          label: 'name',
          width: '40%',
          align: 'left',
          sort: true,
          show: true,
        },
        {
          id: 'start',
          numeric: false,
          disablePadding: false,
          label: 'start',
          width: '6%',
          align: 'left',
          show: true,
        },
      ]
    : [
        {
          id: 'history',
          numeric: false,
          disablePadding: false,
          label: 'history',
          width: '2%',
          align: 'left',
          sort: false,
          show: false,
        },
        {
          id: 'noteCounts',
          numeric: false,
          disablePadding: false,
          label: 'note counts',
          width: '3%',
          align: 'left',
          sort: false,
          show: false,
        },
        {
          id: 'approval',
          numeric: false,
          disablePadding: false,
          label: 'approval',
          width: '3%',
          align: 'left',
          sort: false,
          show: false,
        },
        {
          id: 'nonOrderMed',
          numeric: false,
          disablePadding: false,
          label: 'Order',
          width: '3%',
          align: 'left',
          sort: false,
          show: true,
        },
        {
          id: 'name',
          numeric: false,
          disablePadding: false,
          label: 'name',
          width: '22%',
          align: 'left',
          sort: true,
          show: true,
        },
        {
          id: 'sig',
          numeric: false,
          disablePadding: false,
          label: 'sig',
          width: '22%',
          align: 'left',
          sort: true,
          show: true,
        },
        {
          id: 'quantity',
          numeric: true,
          disablePadding: false,
          label: 'quantity',
          width: '2%',
          align: 'right',
          show: true,
        },
        {
          id: 'start',
          numeric: false,
          disablePadding: false,
          label: 'start',
          width: '6%',
          align: 'left',
          show: true,
        },
        {
          id: 'covered',
          numeric: false,
          disablePadding: false,
          label: coveredSymbol,
          width: '4%',
          symbol: true,
          tip: 'covered',
          align: 'center',
          sort: false,
          show: true,
          hideCol: hidecovered === STRING_TRUE,
        },
        {
          id: 'physician',
          numeric: false,
          disablePadding: false,
          label: 'physician',
          width: '15%',
          align: 'left',
          sort: false,
          show: true,
        },
        {
          id: 'stop',
          numeric: false,
          disablePadding: false,
          label: 'stop',
          width: '6%',
          align: 'center',
          sort: false,
          show: true,
        },
        {
          id: 'stopreason',
          numeric: false,
          disablePadding: false,
          label: '',
          width: '2%',
          align: 'center',
          sort: false,
          show: true,
        },
        {
          id: 'highriskindicated',
          numeric: false,
          disablePadding: false,
          label: '',
          width: '2%',
          align: 'center',
          sort: false,
          show: showStartReasonColumn,
        },
        {
          id: 'startreason',
          numeric: false,
          disablePadding: false,
          label: '',
          width: '2%',
          align: 'center',
          sort: false,
          show: showStartReasonColumn,
        },
        {
          id: 'print',
          numeric: false,
          disablePadding: false,
          label: '',
          tip: 'printTip',
          width: '2%',
          align: 'center',
          sort: false,
          show: true,
        },
      ];

  const TableLabel = (headCell) => {
    if (headCell.show === false) {
      return null;
    }
    if (headCell.symbol) {
      return (
        <Tooltip title={<FormattedMessage {...messages[headCell.tip]} />}>
          {headCell.label}
        </Tooltip>
      );
    }
    if (headCell.sort === false) {
      return headCell.label.charAt(0).toUpperCase() + headCell.label.slice(1);
    }
    return order ? (
      <TableSortLabel
        active={!!(orderBy && orderBy === headCell.id)}
        direction={orderBy && orderBy === headCell.id ? order : 'asc'}
        onClick={createSortHandler(headCell.id)}
      >
        <FormattedMessage {...messages[headCell.label]} />
      </TableSortLabel>
    ) : (
      <TableSortLabel>
        <FormattedMessage {...messages[headCell.label]} />
      </TableSortLabel>
    );
  };

  const className = history ? classes.history : classes.stickyHeader;

  return (
    <StyledTableHead className={className} data-hide={!!history}>
      <TableRow>
        {headCells.map((headCell) =>
          !headCell.hideCol ? (
            <StyledTableCell
              className={className}
              key={headCell.id}
              align={headCell.align}
              // padding="none"
              sortDirection={orderBy && orderBy === headCell.id ? order : false}
              width={headCell.width}
              smallwidget={
                smallWidget && smallWidget.toString()
                  ? smallWidget.toString()
                  : undefined
              }
            >
              {TableLabel(headCell)}
            </StyledTableCell>
          ) : null,
        )}
      </TableRow>
    </StyledTableHead>
  );
};

Headers.propTypes = {
  // classes: PropTypes.object,
  onRequestSort: PropTypes.func,
  order: PropTypes.oneOf(['asc', 'desc']),
  orderBy: PropTypes.string,
  // rowCount: PropTypes.number,
};
